/** @jsx jsx */
import {
	type MutableRefObject,
	memo,
	type ReactNode,
	useState,
	forwardRef,
	type Ref,
	useCallback,
	type KeyboardEventHandler,
	createContext,
	useContext,
} from 'react';
import { css, keyframes, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/import/no-direct-popup
import { Popup, type PopupComponentProps } from '@atlaskit/popup';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCellHoverCallbacks, useCellHoverHandlers } from '../../controllers/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../constants.tsx';
import { PopupContent, type AnalyticsAttributes } from '../popup-content/index.tsx';

export type Props<T extends HTMLElement = HTMLElement> = {
	/**
	 * The content to be rendered in the popup
	 */
	content?: ReactNode;
	/**
	 * Render function to wrap the trigger element.
	 * Ref parameter should be bound to the trigger element.
	 * Aria expanded should be used for interactive popups
	 */
	children: (props: { ref: Ref<T>; ariaExpanded?: boolean }) => ReactNode;
	/**
	 * Ref to the content that can be truncated.
	 * Results in popup being hidden until the content is truncated.
	 */
	contentRef?: MutableRefObject<T | null>;
	/**
	 * Used to preload and load content that comes via and Entrypoint.
	 */
	entryPointActions?: EntryPointActions;
	/**
	 * Whether the popup can be interacted with by mouse/keyboard (including assistive technology).
	 */
	isInteractive?: boolean;
	/**
	 * Whether the popup should get the ref of the nearest truncated element.
	 */
	shouldGetParentRef?: boolean;
	/**
	 * Whether the popup should be fixed width of 300px.
	 */
	isFixedWidth?: boolean;
	/**
	 * Callback to be called when the popup is hovered out.
	 */
	onHoverOut?: () => void;
	/**
	 * Control override to show/hide popo
	 */
	hidePopover?: boolean;
	/**
	 * Optional analytics to pass when the popup is displayed.
	 */
	analyticsAttributes?: AnalyticsAttributes;
};

const PopupComponentInternal = forwardRef<HTMLDivElement, PopupComponentProps>(
	(
		{
			children: popupChildren,
			isReferenceHidden,
			shouldRenderToParent,
			shouldFitContainer,
			...restProps
		},
		ref,
	) => {
		const { isFixedWidth, isInteractive, setIsPopupOpen } = useContext(HoverPopoverContext);

		return !isReferenceHidden ? (
			<div
				css={[
					containerStyles,
					renderToParentStyles,
					isInteractive && interactiveStyles,
					isFixedWidth && fixedWidthStyles,
				]}
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseEnter={() => isInteractive && setIsPopupOpen(true)}
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseLeave={() => isInteractive && setIsPopupOpen(false)}
				{...restProps}
				ref={ref}
			>
				{popupChildren}
			</div>
		) : null;
	},
);

const HoverPopoverContext = createContext<{
	isFixedWidth?: boolean;
	isInteractive?: boolean;
	setIsPopupOpen: (isPopupOpen: boolean) => void;
}>({
	isFixedWidth: false,
	isInteractive: false,
	setIsPopupOpen: noop,
});

const HoverPopoverComponent = <T extends HTMLElement>({
	content,
	children,
	contentRef,
	entryPointActions,
	isInteractive,
	shouldGetParentRef,
	isFixedWidth,
	onHoverOut,
	hidePopover,
	analyticsAttributes,
}: Props<T>) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const togglePopup = useCallback(() => {
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen]);

	useCellHoverCallbacks({
		contentRef,
		onPreload: entryPointActions?.preload,
		onLoad: entryPointActions?.load,
		onPopupToggle: setIsPopupOpen,
		shouldGetParentRef,
		onHoverOut,
		onChangePopupState: togglePopup,
	});

	if (content && isPopupOpen) {
		return (
			<UFOSegment name="jira-list-hover-popover" mode="list">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					<HoverPopoverContext.Provider value={{ isFixedWidth, isInteractive, setIsPopupOpen }}>
						<Popup
							// eslint-disable-next-line jira/import/no-direct-popup
							content={({ update }) => (
								<PopupContent
									isInteractive={isInteractive}
									update={update}
									analyticsAttributes={analyticsAttributes}
								>
									{content}
								</PopupContent>
							)}
							popupComponent={PopupComponentInternal}
							autoFocus={false}
							isOpen={!hidePopover && isPopupOpen}
							onClose={() => setIsPopupOpen(false)}
							trigger={({ ref }) => children({ ref, ariaExpanded: isPopupOpen })}
							placement="bottom-start"
							// TODO JSC-1738 - Add shouldRenderToParent and fix placement of collapsable fields inline edit
						/>
					</HoverPopoverContext.Provider>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}

	return typeof children === 'function' ? children({ ref: null }) : children;
};

// @see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const HoverPopoverWithContext = memo(HoverPopoverComponent) as typeof HoverPopoverComponent;

export type CellHoverRendererProps = {
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onKeyDown?: KeyboardEventHandler<HTMLElement>;
};

export const CellHoverRenderer = ({
	children,
}: {
	children: (props: CellHoverRendererProps) => ReactNode;
}) => {
	const { handleMouseEnter, handleMouseLeave, handleToggle } = useCellHoverHandlers();
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.code === 'Space' || event.code === 'Enter') {
				// Prevent default is needed to prevent the table from scrolling down on space/enter press
				event.preventDefault();
				handleToggle();
			}
		},
		[handleToggle],
	);

	return children({
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onKeyDown: handleKeyDown,
	});
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const interactiveStyles = css({
	marginBlock: token('space.negative.100'),
	paddingBlock: token('space.150'),
	pointerEvents: 'auto',
});

const containerStyles = css({
	marginBlock: token('space.100'),
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});

const renderToParentStyles = css({
	zIndex: 2,
});

const fixedWidthStyles = css({
	width: '300px',
});
